import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useUsersService } from '@/features/users';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';

export interface TourState {
  guide: {
    route: string;
    step: number;
    tourActive: boolean;
  };
  onboarding: {
    completed: boolean;
  };
}

interface UpdateGuideStateParams {
  step: number;
  route: string;
  tourActive: boolean;
  onSuccess?: () => void;
}

interface GuideContextValue {
  guideSettings: TourState;
  startGuide: () => void;
  updateGuideState: (params: UpdateGuideStateParams) => void;
  resetGuide: () => void;
  isLoading?: boolean;
}

const defaultTourState: TourState = {
  guide: {
    route: ROUTES.AUTHORIZED.LANDING,
    tourActive: false,
    step: 0,
  },
  onboarding: {
    completed: true,
  },
};

export const GuideContext = createContext<GuideContextValue>({
  guideSettings: defaultTourState,
  startGuide: () => {
    console.warn('GuideContext not initialized');
  },
  updateGuideState: () => {
    console.warn('GuideContext not initialized');
  },
  resetGuide: () => {
    console.warn('GuideContext not initialized');
  },
});
GuideContext.displayName = 'GuideContext';

interface GuideProviderProps {
  children: ReactNode;
}

export function GuideProvider({ children }: GuideProviderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { getCurrentUserGuide, updateUserGuide } = useUsersService();

  const [guideSettings, setGuideSettings] = useState<TourState>(defaultTourState);
  const [isLoading, setIsLoading] = useState(true);
  const guideSettingsRef = useRef(guideSettings);
  guideSettingsRef.current = guideSettings;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    getCurrentUserGuide(controller.signal)
      .then((data) => {
        if (!cancelled && data) {
          setGuideSettings(data);
        }
      })
      .catch((error: any) => {
        if (!cancelled && error?.name !== 'CanceledError' && error?.name !== 'AbortError') {
          enqueueSnackbar(error?.message || t('Failed to load guide settings'), {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // Intentionally only fetch once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateGuideState = useCallback(
    ({ step, route, tourActive, onSuccess }: UpdateGuideStateParams) => {
      const previous = guideSettingsRef.current;
      const optimistic: TourState = {
        ...previous,
        guide: { step, route, tourActive },
      };

      setGuideSettings(optimistic);

      updateUserGuide({
        guide: { step, route, tourActive },
        onboarding: { completed: previous.onboarding?.completed ?? false },
      })
        .then((response) => {
          if (response) setGuideSettings(response);
          onSuccess?.();
        })
        .catch((error: any) => {
          setGuideSettings(previous);
          enqueueSnackbar(
            error?.response?.data?.message ||
              error?.message ||
              t('Failed to update guide settings'),
            { variant: 'error' },
          );
        });
    },
    [enqueueSnackbar, t, updateUserGuide],
  );

  const startGuide = useCallback(() => {
    const stepIndex = guideSettingsRef.current.guide.step ?? 0;
    const route = stepIndex ? guideSettingsRef.current.guide.route : ROUTES.AUTHORIZED.LANDING;

    if (route !== window.location.pathname) {
      navigate(route);
    }

    updateGuideState({
      step: stepIndex,
      route,
      tourActive: true,
    });
  }, [navigate, updateGuideState]);

  const resetGuide = useCallback(
    () => updateGuideState({ ...defaultTourState.guide }),
    [updateGuideState],
  );

  const value = useMemo<GuideContextValue>(
    () => ({
      guideSettings,
      startGuide,
      updateGuideState,
      resetGuide,
      isLoading,
    }),
    [guideSettings, startGuide, updateGuideState, resetGuide, isLoading],
  );

  return <GuideContext.Provider value={value}>{children}</GuideContext.Provider>;
}

export function useGuideContext(): GuideContextValue {
  const context = useContext(GuideContext);

  if (!context) {
    throw new Error('useGuideContext must be used within a GuideProvider');
  }

  return context;
}
